// Shared pieces of the console profilers (perfProfile.ts, skyProfile.ts): the GPU timer query wrapper and small statistics.

export const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

const sorted = (a: number[]) => [...a].sort((x, y) => x - y);
export const median = (a: number[]) => { const s = sorted(a), n = s.length; return n ? (n % 2 ? s[(n - 1) / 2] : 0.5 * (s[n / 2 - 1] + s[n / 2])) : NaN; };
export const pct = (a: number[], p: number) => { const s = sorted(a); return s.length ? s[Math.min(s.length - 1, Math.floor(p * s.length))] : NaN; };
export const mean = (a: number[]) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : NaN);
export const maxOf = (a: number[]) => a.reduce((m, v) => (v > m ? v : m), -Infinity);
export const sum = (a: number[]) => a.reduce((x, y) => x + y, 0);
/** Sample standard deviation. */
export const sd = (a: number[]) => { if (a.length < 2) return NaN; const m = mean(a); return Math.sqrt(sum(a.map(v => (v - m) * (v - m))) / (a.length - 1)); };
export const fmt = (v: number, d = 2) => (Number.isFinite(v) ? v.toFixed(d) : 'n/a');
export const signed = (v: number, d = 2) => (Number.isFinite(v) ? (v >= 0 ? '+' : '') + v.toFixed(d) : 'n/a');

/**
 * GPU timer queries (EXT_disjoint_timer_query_webgl2). `available` is false when the browser does not offer them.
 * Only one TIME_ELAPSED query can be active at a time, so callers keep their spans sequential (a nested span ends the outer
 * query and starts a new one when it closes).
 */
export class GpuTimer {
  readonly ext: any;
  disjoints = 0;
  constructor(private gl: WebGL2RenderingContext) { this.ext = gl.getExtension('EXT_disjoint_timer_query_webgl2'); }
  get available() { return !!this.ext; }
  /** Clear the disjoint flag, so a later reading is about what follows. */
  reset() { if (this.ext) this.gl.getParameter(this.ext.GPU_DISJOINT_EXT); }
  /** Begin a query; end it with stop(). */
  start(): WebGLQuery {
    const q = this.gl.createQuery()!;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q);
    return q;
  }
  stop() { this.gl.endQuery(this.ext.TIME_ELAPSED_EXT); }
  /** Run `fn` inside a query and return the query; read it later with resolve(). */
  time(fn: () => void): WebGLQuery {
    const q = this.start();
    try { fn(); } finally { this.stop(); }
    return q;
  }
  /** Delete a query that will not be read. */
  discard(q: WebGLQuery) { this.gl.deleteQuery(q); }
  /** Milliseconds for each query (NaN where a result did not come in time). The queries are deleted. */
  async resolve(queries: WebGLQuery[], timeoutMs = 6000): Promise<number[]> {
    const gl = this.gl, out: number[] = [];
    for (const q of queries) {
      const t0 = performance.now();
      let ok = true;
      while (!gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE)) {
        if (performance.now() - t0 > timeoutMs) { ok = false; break; }
        await sleep(2);
      }
      out.push(ok ? (gl.getQueryParameter(q, gl.QUERY_RESULT) as number) / 1e6 : NaN);
      gl.deleteQuery(q);
    }
    if (this.ext && gl.getParameter(this.ext.GPU_DISJOINT_EXT)) this.disjoints++;     // the GPU's clock changed during the batch: the figures are suspect
    return out;
  }
}
